/**
 * Settings Types (TASK-051)
 *
 * Type definitions for Settings & Configuration UI.
 */

// =============================================================================
// Organization Settings
// =============================================================================

export type SubscriptionPlan = 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'CANCELLED' | 'EXPIRED';

/** Cabinet mode (TASK-270): estimator (default) or ecommerce */
export type OrganizationMode = 'estimator' | 'ecommerce';

/** E-commerce module settings (TASK-270) */
export interface EcommerceSettings {
  /** Flat delivery cost added to an order */
  delivery_cost?: number;
  /** Markup applied to catalog prices, percent */
  markup_percent?: number;
  /** Catalog currency, default RUB */
  currency?: string;
  /** Number of products above which the catalog is summarised in the prompt */
  catalog_threshold?: number;
}

/** KAN-783: тип значения атрибута лида. */
export type LeadAttributeType = 'text' | 'bool' | 'choice';

/**
 * KAN-783: настраиваемый атрибут лида: что бот выясняет у клиента.
 * `name` - заголовок столбца CRM и КЛЮЧ значения в карточке клиента
 * (lead_info): после переименования старые значения не видны.
 */
export interface LeadAttribute {
  id: string;
  name: string;
  /** Подсказка модели: что именно спросить или откуда взять. */
  hint: string;
  type: LeadAttributeType;
  /** Варианты значения, только для type === 'choice'. */
  options: string[];
  /** Столбец на доске CRM. */
  show_in_crm: boolean;
  /** Непустое значение поднимает клиента в "Требуют внимания". */
  attention: boolean;
  /** Правило ответа бота, связанное с атрибутом. */
  reply_rule: string;
}

export interface OrganizationSettings {
  name: string;
  slug: string;
  logo_url: string | null;
  subscription_plan: SubscriptionPlan;
  subscription_status: SubscriptionStatus;
  trial_end_date: string | null;
  max_users: number;
  max_estimates_per_month: number;
  max_data_sources: number;
  max_storage_mb: number;
  /** Company name spoken by the bot in greetings */
  company_name?: string;
  /** Company description for bot operator mode (TASK-135) */
  company_description?: string;
  /** Cabinet mode: estimator (default) or ecommerce (TASK-270) */
  mode?: OrganizationMode;
  /** E-commerce module knobs (TASK-270) */
  ecommerce_settings?: EcommerceSettings;
  /** KAN-742: organization contacts (go into the bot prompt) */
  website?: string;
  phone?: string;
  company_email?: string;
  telegram?: string;
  max_contact?: string;
  whatsapp?: string;
  /** KAN-783: атрибуты лида; старый backend поле не шлёт. */
  lead_attributes?: LeadAttribute[];
  /** KAN-783: названия, занятые встроенными полями (не могут быть атрибутом); без поля - список фронта. */
  lead_attribute_reserved_names?: string[];
  /** KAN-811: каких клиентов видит менеджер (роль MANAGER); старый backend поле не шлёт. */
  manager_visibility?: ManagerVisibility;
}

/** KAN-811: свои и ничьи клиенты (по умолчанию) или все клиенты кабинета. */
export type ManagerVisibility = 'own_and_unassigned' | 'all';

export const DEFAULT_MANAGER_VISIBILITY: ManagerVisibility = 'own_and_unassigned';

export const MANAGER_VISIBILITY_OPTIONS: ReadonlyArray<{ value: ManagerVisibility; label: string }> = [
  { value: 'own_and_unassigned', label: 'Свои и ничьи' },
  { value: 'all', label: 'Все клиенты кабинета' },
];

/** KAN-742: contact fields of the organization, in prompt order. */
export type OrganizationContactField =
  | 'website'
  | 'phone'
  | 'company_email'
  | 'telegram'
  | 'max_contact'
  | 'whatsapp';

export interface UpdateOrganizationPayload {
  name?: string;
  logo?: File | null;
  /** Company name spoken by the bot in greetings */
  company_name?: string;
  /** Company description for bot operator mode (TASK-135) */
  company_description?: string;
  /** Cabinet mode: estimator or ecommerce (TASK-270) */
  mode?: OrganizationMode;
  /** E-commerce module knobs (TASK-270) */
  ecommerce_settings?: EcommerceSettings;
  /** KAN-742: organization contacts */
  website?: string;
  phone?: string;
  company_email?: string;
  telegram?: string;
  max_contact?: string;
  whatsapp?: string;
  /** KAN-783: атрибуты лида. */
  lead_attributes?: LeadAttribute[];
  /** KAN-811: каких клиентов видит менеджер. */
  manager_visibility?: ManagerVisibility;
}

// =============================================================================
// AI Settings
// =============================================================================

export type AIModel =
  | 'gpt-4o'
  | 'gpt-4-turbo'
  | 'gpt-4'
  | 'gpt-3.5-turbo'
  | 'claude-3-opus'
  | 'claude-3-sonnet'
  | 'claude-3-haiku';

export type EmbeddingModel =
  | 'text-embedding-3-small'
  | 'text-embedding-3-large'
  | 'text-embedding-ada-002';

export interface AISettings {
  model: AIModel;
  temperature: number;
  max_tokens: number;
  embedding_model: EmbeddingModel;
  vector_db_collection: string;
  estimation_methodology?: string;
}

export interface UpdateAIPayload {
  model?: AIModel;
  temperature?: number;
  max_tokens?: number;
  embedding_model?: EmbeddingModel;
  estimation_methodology?: string;
}

// =============================================================================
// Integrations Settings
// =============================================================================

export interface IntegrationsSettings {
  openrouter_api_key: string;
  telegram_bot_token: string;
  telegram_bot_enabled: boolean;
  webhook_url: string;
  webhook_secret: string;
}

export interface UpdateIntegrationsPayload {
  openrouter_api_key?: string;
  telegram_bot_token?: string;
  telegram_bot_enabled?: boolean;
  webhook_url?: string;
  webhook_secret?: string;
}

// =============================================================================
// System Settings
// =============================================================================

export type Language = 'ru' | 'en';
export type Currency = 'RUB' | 'USD' | 'EUR';
export type DateFormat = 'DD.MM.YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

export interface SystemSettings {
  language: Language;
  timezone: string;
  date_format: DateFormat;
  currency: Currency;
  pagination_size: number;
  enable_notifications: boolean;
  enable_audit_log: boolean;
}

export interface UpdateSystemPayload {
  language?: Language;
  timezone?: string;
  date_format?: DateFormat;
  currency?: Currency;
  pagination_size?: number;
  enable_notifications?: boolean;
  enable_audit_log?: boolean;
}

// =============================================================================
// Combined Settings
// =============================================================================

export interface Settings {
  organization: OrganizationSettings;
  ai: AISettings;
  integrations: IntegrationsSettings;
  system: SystemSettings;
  user_can_manage: boolean;
}

// =============================================================================
// Constants
// =============================================================================

export const AI_MODELS: { value: AIModel; label: string }[] = [
  { value: 'gpt-4o', label: 'gpt-4o' },
  { value: 'gpt-4-turbo', label: 'gpt-4-turbo' },
  { value: 'gpt-4', label: 'gpt-4' },
  { value: 'gpt-3.5-turbo', label: 'gpt-3.5-turbo' },
  { value: 'claude-3-opus', label: 'claude-3-opus' },
  { value: 'claude-3-sonnet', label: 'claude-3-sonnet' },
  { value: 'claude-3-haiku', label: 'claude-3-haiku' },
];

export const EMBEDDING_MODELS: { value: EmbeddingModel; label: string }[] = [
  { value: 'text-embedding-3-small', label: 'Text Embedding 3 Small' },
  { value: 'text-embedding-3-large', label: 'Text Embedding 3 Large' },
  { value: 'text-embedding-ada-002', label: 'Text Embedding Ada 002' },
];

export const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'ru', label: 'Russian' },
  { value: 'en', label: 'English' },
];

export const CURRENCIES: { value: Currency; label: string }[] = [
  { value: 'RUB', label: 'Russian Ruble (RUB)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
];

export const DATE_FORMATS: { value: DateFormat; label: string }[] = [
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2025)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' },
];

export const SUBSCRIPTION_PLANS: { value: SubscriptionPlan; label: string }[] = [
  { value: 'STARTER', label: 'Starter' },
  { value: 'PROFESSIONAL', label: 'Professional' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
];

export const SUBSCRIPTION_STATUSES: { value: SubscriptionStatus; label: string }[] = [
  { value: 'TRIAL', label: 'Trial' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];
